import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Menu, X, MapPin, Phone, Mail, Clock, 
  UtensilsCrossed, Coffee, ChefHat, Cake,
  Send, Instagram, Facebook, Linkedin,
  Building2, ArrowUpRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";
import shaakImage from "@/lib/image/shaak.jpg";
import ecologicoImage from "@/lib/image/ecologico.jpg";
import longaprilImage from "@/lib/image/longapril.jpg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  UtensilsCrossed: UtensilsCrossed,
  Coffee: Coffee,
  ChefHat: ChefHat,
  Cake: Cake,
};

// Custom hook for scroll reveal animation
const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || "0px 0px -50px 0px",
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
};

// Reusable Reveal component
const Reveal = ({ children, className = "", direction = "up", delay = 0, threshold = 0.1 }) => {
  const [ref, isVisible] = useScrollReveal({ threshold });
  
  const directionClass = {
    up: "reveal",
    left: "reveal-left",
    right: "reveal-right",
    scale: "reveal-scale",
    fade: "reveal-fade",
  }[direction] || "reveal";

  const delayClass = delay > 0 ? `stagger-${Math.min(delay, 8)}` : "";

  return (
    <div
      ref={ref}
      className={`${directionClass} ${delayClass} ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#beranda", label: "Beranda" },
    { href: "#tentang", label: "Tentang" },
    { href: "#layanan", label: "Layanan" },
    { href: "#portofolio", label: "Portofolio" },
    { href: "#kontak", label: "Kontak" },
  ];

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-light border-b border-[#E5E5E5]" : "bg-transparent"
        }`}
      >
        <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
          <a href="#beranda" className="flex items-center gap-3" data-testid="logo">
            <div className="gold-dot"></div>
            <span className="font-['Cormorant_Garamond'] text-xl md:text-2xl font-medium text-[#1A1A1A] tracking-tight">
              Helena Chloe Gracia
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isOpen && (
        <div 
          className="mobile-menu md:hidden" 
          data-testid="mobile-menu"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-5 right-6 p-2"
            data-testid="mobile-menu-close"
          >
            <X size={28} />
          </button>
          {navLinks.map((link, index) => (
            <a
              key={link.href}
              href={link.href}
              className="mobile-nav-link animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
              onClick={() => setIsOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="beranda" className="min-h-screen flex items-center pt-20" data-testid="hero-section">
      <div className="px-6 md:px-12 lg:px-24 py-16 md:py-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            <div className="section-label animate-fade-in-up">Sejak 2023</div>
            <h1 className="section-title text-5xl sm:text-6xl lg:text-7xl mt-6 mb-8 animate-fade-in-up animation-delay-200">
              Pengalaman
              <span className="text-gold-gradient"> Kuliner</span>
              <br />Premium
            </h1>
            <p className="section-body text-lg md:text-xl mb-10 max-w-lg animate-fade-in-up animation-delay-400">
              Menghadirkan standar tertinggi dalam industri F&B dengan sentuhan 
              elegan dan cita rasa autentik dari Kalimantan hingga Jakarta.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-600">
              <a href="#portofolio">
                <Button 
                  className="bg-[#1A1A1A] text-[#FAFAFA] rounded-none px-8 py-6 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#C9A962] transition-all duration-400"
                  data-testid="hero-cta-portfolio"
                >
                  Jelajahi Portfolio
                </Button>
              </a>
              <a href="#kontak">
                <Button 
                  variant="outline"
                  className="bg-transparent text-[#1A1A1A] border border-[#1A1A1A] rounded-none px-8 py-6 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#1A1A1A] hover:text-[#FAFAFA] transition-all duration-400"
                  data-testid="hero-cta-contact"
                >
                  Hubungi Kami
                </Button>
              </a>
            </div>
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2 animate-scale-in animation-delay-300">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1636928837218-f2b56d5a7861?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYWZlJTIwYmFyJTIwcmVzdGF1cmFudCUyMGludGVyaW9yfGVufDB8fHx8MTc3NTM4MDkzOXww&ixlib=rb-4.1.0&q=85"
                  alt="Luxury cafe bar restaurant interior"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Gold accent frame */}
              <div className="absolute -bottom-4 -left-4 w-full h-full border border-[#C9A962] -z-10 hidden lg:block"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="tentang" className="py-24 md:py-32 bg-[#F5F5F5]" data-testid="about-section">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <Reveal direction="left">
            <div className="img-hover-zoom">
              <div className="aspect-[4/3] overflow-hidden relative">
                <img
                  src="https://images.pexels.com/photos/33772745/pexels-photo-33772745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Elegant cafe interior"
                  className="w-full h-full object-cover"
                />
                {/* Gold corner accents */}
                <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[#C9A962]"></div>
                <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[#C9A962]"></div>
              </div>
            </div>
          </Reveal>

          {/* Content */}
          <div>
            <Reveal direction="up">
              <div className="section-label">Tentang Kami</div>
            </Reveal>
            <Reveal direction="up" delay={1}>
              <h2 className="section-title text-4xl sm:text-5xl mt-6 mb-8">
                Dedikasi untuk Keunggulan
              </h2>
            </Reveal>
            <Reveal direction="up" delay={2}>
              <div className="space-y-6 section-body">
                <p>
                  <strong className="text-[#1A1A1A] font-medium">PT Helena Chloe Gracia </strong>telah merawat rasa dan kepercayaan di Kalimantan sebagai pionir kuliner premium. Fokus kami sederhana namun mendalam: menyajikan pengalaman F&B terbaik yang memadukan keaslian cita rasa dengan keramahan layanan Terbaik." 
                </p>
                <p>
                  Kami percaya bahwa setiap hidangan adalah cerita. Dengan menggabungkan kearifan lokal dan sentuhan layanan internasional, kami menghadirkan pengalaman bersantap yang istimewa untuk Anda. Perjalanan rasa ini kini berlanjut ke Jakarta dan berbagai kota besar lainnya.
                </p>
              </div>
            </Reveal>

            {/* Stats */}
            <Reveal direction="up" delay={3}>
              <div className="grid grid-cols-3 gap-8 mt-12 pt-12 border-t border-[#E5E5E5]">
                <div>
                  <span className="stat-number text-5xl md:text-6xl">5+</span>
                  <p className="text-sm text-[#666666] mt-2 font-light">Outlet Aktif</p>
                </div>
                <div>
                  <span className="stat-number text-5xl md:text-6xl">6+</span>
                  <p className="text-sm text-[#666666] mt-2 font-light">Tahun Berpengalaman</p>
                </div>
                <div>
                  <span className="stat-number text-5xl md:text-6xl">10K</span>
                  <p className="text-sm text-[#666666] mt-2 font-light">Tamu Dilayani</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    // Use local data instead of backend API
    const localServices = [
      {
        "id": "1",
        "title": "Fine Dining",
        "description": "Pengalaman kuliner premium dengan hidangan Indonesia dan internasional yang disajikan dengan standar tertinggi",
        "icon": "UtensilsCrossed"
      },
      {
        "id": "2",
        "title": "Cafe & Bistro",
        "description": "Konsep kafe modern dengan menu kopi specialty, brunch, dan hidangan kasual yang Instagram-worthy",
        "icon": "Coffee"
      },
      {
        "id": "3",
        "title": "Catering & Events",
        "description": "Layanan catering profesional untuk berbagai acara dari pernikahan hingga event korporat",
        "icon": "ChefHat"
      },
      {
        "id": "4",
        "title": "Bakery & Patisserie",
        "description": "Produk roti dan kue artisan dengan bahan premium dan teknik pembuatan tradisional Eropa",
        "icon": "Cake"
      }
    ];
    setServices(localServices);
  }, []);

  return (
    <section id="layanan" className="py-24 md:py-32" data-testid="services-section">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Reveal direction="up">
            <div className="section-label justify-center">Layanan Kami</div>
          </Reveal>
          <Reveal direction="up" delay={1}>
            <h2 className="section-title text-4xl sm:text-5xl mt-6 mb-6">
              Solusi Kuliner Premium
            </h2>
          </Reveal>
          <Reveal direction="up" delay={2}>
            <p className="section-body">
              Dari fine dining hingga catering eksklusif, kami menyediakan layanan F&B 
              dengan standar kualitas tertinggi untuk setiap kebutuhan Anda.
            </p>
          </Reveal>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || UtensilsCrossed;
            return (
              <Reveal key={service.id} direction="up" delay={index + 1}>
                <div
                  className="service-card h-full"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="flex items-start gap-6">
                    <div className="p-4 border border-[#C9A962]">
                      <IconComponent size={28} strokeWidth={1} className="text-[#C9A962]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl mb-3 text-[#1A1A1A] font-medium">
                        {service.title}
                      </h3>
                      <p className="section-body text-sm">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const [restaurants, setRestaurants] = useState([]);

  useEffect(() => {
    // Use local data instead of backend API
    const localRestaurants = [
      {
        "id": "1",
        "name": "Blu Shaak",
        "location": "Central Park, Jakarta",
        "type": "Cafe",
        "description": "Restoran fine dining dengan konsep modern yang menyajikan masakan Indonesia kontemporer",
        "image_url": shaakImage
      },
      {
        "id": "2",
        "name": "Long April",
        "location": "Samarinda, Kalimantan Timur",
        "type": "Cafe & Bistro",
        "description": "Kafe dengan suasana cozy yang menyajikan kopi specialty dan hidangan fusion",
        "image_url": longaprilImage
      },
      {
        "id": "3",
        "name": "Ecologico ",
        "location": "Samarinda, Kalimantan Timur",
        "type": "Restaurant ",
        "description": "Lounge premium dengan live music dan cocktail signature",
        "image_url": ecologicoImage
      },
      {
        "id": "4",
        "name": "ONG PALACE",
        "location": "Samarinda, Kalimantan Timur",
        "type": "Chinese Restaurant",
        "description": "Layanan catering premium untuk event korporat dan pernikahan",
        "image_url": "https://images.unsplash.com/photo-1750943082012-efe6d2fd9e45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxmaW5lJTIwZGluaW5nJTIwY2hlZiUyMHBsYXRpbmd8ZW58MHx8fHwxNzc1Mzc2MzA2fDA&ixlib=rb-4.1.0&q=85"
      },
      {
        "id": "5",
        "name": "Silversea Patisserie",
        "location": "Samarinda, Kalimantan Timur",
        "type": "Bakery & Patisserie",
        "description": "Toko kue dan pastry premium dengan resep original dan bahan berkualitas",
        "image_url": "https://images.unsplash.com/photo-1769690093961-5241cea477e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjYWZlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NTM3NjMwNnww&ixlib=rb-4.1.0&q=85"
      }
    ];
    setRestaurants(localRestaurants);
  }, []);

  // Tetris grid configuration
  const gridConfig = [
    "col-span-12 md:col-span-7 row-span-2",
    "col-span-12 md:col-span-5",
    "col-span-12 md:col-span-5",
    "col-span-12 md:col-span-6",
    "col-span-12 md:col-span-6",
  ];

  return (
    <section id="portofolio" className="py-24 md:py-32 bg-[#1A1A1A]" data-testid="portfolio-section">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <Reveal direction="up">
            <div className="section-label text-[#C9A962]">Portofolio</div>
          </Reveal>
          <Reveal direction="up" delay={1}>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl mt-6 mb-6 text-[#FAFAFA] font-normal">
              Outlet Kami
            </h2>
          </Reveal>
          <Reveal direction="up" delay={2}>
            <p className="text-[#999999] font-light leading-relaxed">
              Temukan restoran dan kafe kami yang tersebar di berbagai kota, 
              masing-masing dengan karakteristik dan pengalaman unik.
            </p>
          </Reveal>
        </div>

        {/* Portfolio Grid */}
        <div className="portfolio-grid">
          {restaurants.map((restaurant, index) => (
            <Reveal 
              key={restaurant.id} 
              direction="scale" 
              delay={index + 1}
              className={`${gridConfig[index] || "col-span-12 md:col-span-4"}`}
            >
              <div
                className="group cursor-pointer h-full"
                data-testid={`portfolio-item-${restaurant.id}`}
              >
                <div className="relative h-full min-h-[280px] md:min-h-[320px] overflow-hidden">
                  <img
                    src={restaurant.image_url}
                    alt={restaurant.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent opacity-90"></div>
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <span className="inline-block px-3 py-1 border border-[#C9A962] text-[#C9A962] text-[10px] uppercase tracking-[0.15em] mb-3">
                      {restaurant.type}
                    </span>
                    <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#FAFAFA] mb-2 font-medium">
                      {restaurant.name}
                    </h3>
                    <div className="flex items-center gap-2 text-[#999999] text-sm font-light">
                      <MapPin size={14} className="text-[#C9A962]" />
                      <span>{restaurant.location}</span>
                    </div>
                  </div>

                  {/* Hover arrow */}
                  <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                    <div className="p-3 bg-[#C9A962]">
                      <ArrowUpRight size={20} className="text-[#1A1A1A]" />
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Pesan berhasil dikirim! Kami akan segera menghubungi Anda.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Gagal mengirim pesan. Silakan coba lagi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Building2,
      title: "Kantor Pusat",
      content: (
        <>
          Jl. HR. Rasuna Said, Gedung Cyber 2 Tower<br />
          Lantai 21 Blok X5 Nomor 13<br />
          Kuningan Timur, Setiabudi<br />
          Jakarta Selatan, DKI Jakarta
        </>
      ),
    },
    {
      icon: Phone,
      title: "Telepon",
      content: "+62 81255229727",
    },
    {
      icon: Mail,
      title: "Email",
      content: "info@helenachloegracia.co.id",
    },
    {
      icon: Clock,
      title: "Jam Operasional",
      content: (
        <>
          Senin - Jumat: 09:00 - 17:00 WIB<br />
          Sabtu: 09:00 - 17:00 WIB
        </>
      ),
    },
  ];

  return (
    <section id="kontak" className="py-24 md:py-32" data-testid="contact-section">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Info */}
          <div>
            <Reveal direction="up">
              <div className="section-label">Hubungi Kami</div>
            </Reveal>
            <Reveal direction="up" delay={1}>
              <h2 className="section-title text-4xl sm:text-5xl mt-6 mb-8">
                Mari Berdiskusi
              </h2>
            </Reveal>
            <Reveal direction="up" delay={2}>
              <p className="section-body mb-12">
                Kami siap membantu kebutuhan kuliner dan bisnis Anda. 
                Hubungi kami untuk konsultasi, kerjasama, atau informasi lebih lanjut.
              </p>
            </Reveal>

            {/* Contact Details */}
            <div className="space-y-8">
              {contactInfo.map((item, index) => (
                <Reveal key={item.title} direction="left" delay={index + 1}>
                  <div className="flex items-start gap-5">
                    <div className="p-3 border border-[#C9A962]">
                      <item.icon size={22} strokeWidth={1} className="text-[#C9A962]" />
                    </div>
                    <div>
                      <h4 className="font-['Cormorant_Garamond'] text-xl text-[#1A1A1A] mb-2">{item.title}</h4>
                      <p className="section-body text-sm leading-relaxed">{item.content}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <Reveal direction="right">
            <div className="bg-[#F5F5F5] p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-[10px] uppercase tracking-[0.2em] text-[#C9A962] font-semibold mb-3">
                    Nama Lengkap
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input bg-transparent"
                    placeholder="Masukkan nama lengkap"
                    data-testid="contact-input-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[10px] uppercase tracking-[0.2em] text-[#C9A962] font-semibold mb-3">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input bg-transparent"
                    placeholder="Masukkan alamat email"
                    data-testid="contact-input-email"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-[10px] uppercase tracking-[0.2em] text-[#C9A962] font-semibold mb-3">
                    Pesan
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="form-input bg-transparent resize-none"
                    placeholder="Tuliskan pesan Anda"
                    data-testid="contact-input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C9A962] text-[#1A1A1A] rounded-none px-8 py-6 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#B8944D] transition-all duration-400 flex items-center justify-center gap-3"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    "Mengirim..."
                  ) : (
                    <>
                      Kirim Pesan <Send size={16} />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer-dark py-16 md:py-24" data-testid="footer">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <Reveal direction="up" className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 bg-[#C9A962]"></div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#FAFAFA] font-normal">
                Helena Chloe Gracia
              </h3>
            </div>
            <p className="text-[#999999] font-light max-w-md leading-relaxed">
              Menghadirkan pengalaman kuliner premium dengan dedikasi terhadap 
              kualitas dan keunggulan. Dari Kalimantan untuk Indonesia.
            </p>
            {/* Social */}
            <div className="flex gap-4 mt-8">
              <a href="#" className="p-3 border border-[#333333] hover:border-[#C9A962] hover:bg-[#C9A962] transition-all duration-300 group" data-testid="social-instagram">
                <Instagram size={18} className="text-[#FAFAFA] group-hover:text-[#1A1A1A]" />
              </a>
              <a href="#" className="p-3 border border-[#333333] hover:border-[#C9A962] hover:bg-[#C9A962] transition-all duration-300 group" data-testid="social-facebook">
                <Facebook size={18} className="text-[#FAFAFA] group-hover:text-[#1A1A1A]" />
              </a>
              <a href="#" className="p-3 border border-[#333333] hover:border-[#C9A962] hover:bg-[#C9A962] transition-all duration-300 group" data-testid="social-linkedin">
                <Linkedin size={18} className="text-[#FAFAFA] group-hover:text-[#1A1A1A]" />
              </a>
            </div>
          </Reveal>

          {/* Quick Links */}
          <Reveal direction="up" delay={1}>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] font-semibold mb-6">Navigasi</h4>
            <ul className="space-y-3">
              <li><a href="#beranda" className="footer-link">Beranda</a></li>
              <li><a href="#tentang" className="footer-link">Tentang Kami</a></li>
              <li><a href="#layanan" className="footer-link">Layanan</a></li>
              <li><a href="#portofolio" className="footer-link">Portofolio</a></li>
              <li><a href="#kontak" className="footer-link">Kontak</a></li>
            </ul>
          </Reveal>

          {/* Contact */}
          <Reveal direction="up" delay={2}>
            <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] font-semibold mb-6">Kontak</h4>
            <ul className="space-y-3 text-[#999999] font-light text-sm">
              <li>+62 21 5678 9012</li>
              <li>info@helenachloegracia.com</li>
              <li className="leading-relaxed">
                Gedung Cyber 2 Tower, Lt. 21<br />
                Jakarta Selatan
              </li>
            </ul>
          </Reveal>
        </div>

        {/* Bottom */}
        <Reveal direction="fade">
          <div className="pt-8 border-t border-[#333333] flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#666666] text-xs font-light tracking-wide">
              &copy; {currentYear} PT. Helena Chloe Gracia. All rights reserved.
            </p>
            <p className="text-[#666666] text-xs font-light tracking-wide">
              Crafted with passion for culinary excellence
            </p>
          </div>
        </Reveal>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <PortfolioSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
